import { useApi, useRealtime, useRealtimeEvent } from '@deskworks/client/react';
import { Avatar, Button, Icon, IconButton, toast } from '@deskworks/ui';
import { RoomAudioRenderer } from '@livekit/components-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { memberBackend } from './backends.js';
import { MeetingCall, type CallState } from './engine.js';
import { loadPrefs } from './media.js';
import './meetings.css';

export interface ActiveCall {
  occurrenceId: string;
  title: string;
  startedAt: number;
}

interface CallContextValue {
  call: MeetingCall;
  /** The meeting the person is in (or waiting to join), if any. */
  active: ActiveCall | null;
}

const CallContext = createContext<CallContextValue | null>(null);

export function useCallEngine(call: MeetingCall): CallState {
  return useSyncExternalStore(call.subscribe, call.getSnapshot, call.getSnapshot);
}

/** Polite screen-reader announcements for a call (admission, removal, hand raise, reconnects). */
export function CallAnnouncer({ call, toastImportant }: { call: MeetingCall; toastImportant?: boolean }) {
  const [msg, setMsg] = useState<{ text: string; n: number }>({ text: '', n: 0 });
  const toastRef = useRef(toastImportant);
  toastRef.current = toastImportant;
  useEffect(
    () =>
      call.onAnnounce((text, important) => {
        setMsg((m) => ({ text, n: m.n + 1 }));
        if (important && toastRef.current) toast(text);
      }),
    [call],
  );
  return (
    <div className="dw-visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {/* Changing key re-announces repeated messages. */}
      <span key={msg.n}>{msg.text}</span>
    </div>
  );
}

/** Plays every remote participant's audio while in a call, on any screen. */
export function CallAudio({ state }: { state: CallState }) {
  return state.room ? <RoomAudioRenderer room={state.room} /> : null;
}

/**
 * Holds the person's meeting call for the whole signed-in app, so audio stays connected while they
 * move between modules (shown as a compact strip with Return to meeting).
 */
export function CallProvider({ children }: { children: ReactNode }) {
  const [call] = useState(() => new MeetingCall());
  const state = useCallEngine(call);
  const location = useLocation();
  const realtime = useRealtime();
  const onRoomPage = !!state.occurrenceId && location.pathname === `/meetings/${state.occurrenceId}`;

  useRealtimeEvent('meeting.changed', (n) => call.notifyChanged(n.occurrenceId));

  // Hosts away from the meeting screen hear about people waiting in the lobby.
  const navigate = useNavigate();
  const waitingRef = useRef(0);
  useRealtimeEvent('meeting.lobby', (n) => {
    const s = call.getSnapshot();
    if (!call.active || n.occurrenceId !== s.occurrenceId) return;
    const prev = waitingRef.current;
    waitingRef.current = n.waiting;
    if (n.waiting > prev && location.pathname !== `/meetings/${n.occurrenceId}`) {
      const text = n.waiting === 1 ? `Someone is waiting to join ${s.title}` : `${n.waiting} people are waiting to join ${s.title}`;
      call.announce(`${text}.`);
      toast(text, { action: { label: 'View', onClick: () => navigate(`/meetings/${n.occurrenceId}`) } });
    }
  });
  useEffect(() => realtime?.onReconnected(() => state.occurrenceId && call.notifyChanged(state.occurrenceId)), [realtime, call, state.occurrenceId]);

  useEffect(() => {
    const onHide = () => call.pageHide();
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [call]);

  // Signing out (or losing the session) ends the call.
  useEffect(() => () => void call.leave('idle'), [call]);

  const active = useMemo<ActiveCall | null>(
    () =>
      call.active && state.occurrenceId
        ? { occurrenceId: state.occurrenceId, title: state.title, startedAt: state.joinedAt ?? Date.now() }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.phase, state.occurrenceId, state.title, state.joinedAt],
  );
  const value = useMemo(() => ({ call, active }), [call, active]);
  return (
    <CallContext.Provider value={value}>
      {children}
      <CallAudio state={state} />
      <CallAnnouncer call={call} toastImportant={!onRoomPage} />
    </CallContext.Provider>
  );
}

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error('CallProvider missing');
  return ctx;
}

export function formatElapsed(ms: number) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

export function useElapsed(since: number | null | undefined) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!since) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [since]);
  return since ? formatElapsed(now - since) : '';
}

/** Compact strip shown while in a call but away from its meeting screen. */
export function CallStrip() {
  const { call } = useCall();
  const state = useCallEngine(call);
  const navigate = useNavigate();
  const location = useLocation();
  const elapsed = useElapsed(state.joinedAt);
  if (!call.active || !state.occurrenceId || location.pathname === `/meetings/${state.occurrenceId}`) return null;
  const status =
    state.phase === 'lobby'
      ? 'Waiting to be let in'
      : state.phase === 'reconnecting'
        ? 'Reconnecting…'
        : state.phase === 'joining' || state.phase === 'connecting'
          ? 'Joining…'
          : elapsed;
  return (
    <div className="dw-callstrip" role="region" aria-label="Active meeting">
      <Icon name="meetings" size={18} />
      <span className="dw-callstrip__title">{state.title || 'Meeting'}</span>
      <span className="dw-callstrip__time" aria-live="off">
        {status}
      </span>
      {state.room ? (
        <IconButton
          className="dw-callstrip__btn"
          icon={state.mic ? 'microphone' : 'mic-off'}
          label={state.mic ? 'Mute microphone' : 'Unmute microphone'}
          aria-pressed={state.mic}
          onClick={() => void call.setMic(!state.mic)}
        />
      ) : null}
      {state.audioBlocked ? (
        <Button size="sm" onClick={() => void call.enableAudio()}>
          Turn on sound
        </Button>
      ) : null}
      <Button size="sm" variant="primary" onClick={() => navigate(`/meetings/${state.occurrenceId}`)}>
        Return to meeting
      </Button>
      <IconButton className="dw-callstrip__leave" icon="hang-up" label="Leave meeting" onClick={() => void call.leave('idle')} />
    </div>
  );
}

/** In-app ringing for Meet now from a direct or group chat. Stops when answered on any device. */
export function IncomingCall() {
  const realtime = useRealtime();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { call, active } = useCall();
  const [ringing, setRinging] = useState<{ occurrenceId: string; from: string; title: string } | null>(null);

  useRealtimeEvent('call.ringing', (n) => {
    if (active?.occurrenceId === n.occurrenceId) return;
    setRinging({ occurrenceId: n.occurrenceId, from: n.from, title: n.title });
  });
  useRealtimeEvent('call.answered', (n) => {
    setRinging((r) => (r?.occurrenceId === n.occurrenceId ? null : r));
  });

  useEffect(() => {
    if (!ringing) return;
    const t = setTimeout(() => setRinging(null), 45_000);
    return () => clearTimeout(t);
  }, [ringing]);

  const answer = useCallback(() => {
    if (!ringing) return;
    realtime?.send({ type: 'call.answered', occurrenceId: ringing.occurrenceId });
    setRinging(null);
    // Answering is the opt-in: join straight away with this device's saved camera/mic choices.
    void call.start(memberBackend(api, { occurrenceId: ringing.occurrenceId }), loadPrefs(), ringing.title);
    navigate(`/meetings/${ringing.occurrenceId}`);
  }, [ringing, realtime, navigate, call, api]);

  if (!ringing || location.pathname === `/meetings/${ringing.occurrenceId}`) return null;
  return (
    <div className="dw-ringing" role="alertdialog" aria-labelledby="dw-ringing-title" aria-describedby="dw-ringing-desc">
      <Avatar initials={ringing.from.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()} size={44} />
      <div className="dw-ringing__text">
        <strong id="dw-ringing-title">{ringing.from} is calling</strong>
        <span id="dw-ringing-desc">{ringing.title}</span>
      </div>
      <Button variant="danger" icon="hang-up" onClick={() => setRinging(null)}>
        Decline
      </Button>
      <Button variant="primary" icon="meetings" onClick={answer} data-autofocus>
        Join
      </Button>
    </div>
  );
}
